import { createContext, type ReactNode, useContext, useState } from "react";

type SelectedFilters = Record<string, string[]>;

interface FilterPayload {
  name: string;
  operator: string;
  value: string;
}

interface AssignOwnerFilterContextType {
  selectedFilters: SelectedFilters;
  setSelectedFilters: (
    filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters),
  ) => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  constructFiltersPayload: (filters: SelectedFilters) => FilterPayload[];
}

const AssignOwnerFilterContext = createContext<
  AssignOwnerFilterContextType | undefined
>(undefined);

export function AssignOwnerFilterProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [searchValue, setSearchValue] = useState<string>("");

  const constructFiltersPayload = (
    filters: SelectedFilters,
  ): FilterPayload[] => {
    return Object.entries(filters)
      .filter(([, values]) => values.length > 0)
      .flatMap(([field, values]) =>
        values.map((value) => ({
          name: field,
          operator: "EQ",
          value,
        })),
      );
  };

  return (
    <AssignOwnerFilterContext.Provider
      value={{
        selectedFilters,
        setSelectedFilters,
        searchValue,
        setSearchValue,
        constructFiltersPayload,
      }}
    >
      {children}
    </AssignOwnerFilterContext.Provider>
  );
}

export function useAssignOwnerFilter() {
  const context = useContext(AssignOwnerFilterContext);
  if (context === undefined) {
    throw new Error(
      "useAssignOwnerFilter must be used within an AssignOwnerFilterProvider",
    );
  }
  return context;
}
